// pages/HolidaysPage.tsx (updated version)
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Upload } from 'lucide-react';
import PageLayout from '../components/PageLayout';
import HolidayList from '../components/holidays/HolidayList';
import HolidayFormModal from '../components/holidays/HolidayFormModal';
import TourPlanList from '../components/holidays/TourPlanList';
import AddTourPlanModal, { TourPlanRequest } from '../components/holidays/AddTourPlanModal';
import ApproveTourPlanModal from '../components/holidays/ApproveTourPlanModal';
import ViewTourPlanModal from '../components/holidays/ViewTourPlanModal';
import { HolidayFormData, TourPlan, TourPlanFormData, HolidaysPageProps } from '../components/common/types';
import Holiday from '../types/holiday';
import { addHoliday, deleteHoliday, editHoliday, getAllHolidays } from '../api/holiday';
import { useAuth } from '../context/AuthContext';
import { useWorkCalendar } from '../hooks/useWorkCalendar';
import { updateLeave } from '../api/tour-plan';
import { useApprovalFlow } from '../hooks/useApprovalFlow';
import { toast } from '../utils/toast';

const HolidaysPage: React.FC<HolidaysPageProps> = ({ onLogout, onNavigate }) => {
  const [activeTab, setActiveTab] = useState<'holiday' | 'work'>('holiday');
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddHolidayModalOpen, setIsAddHolidayModalOpen] = useState(false);
  const [isAddTourPlanModalOpen, setIsAddTourPlanModalOpen] = useState(false);
  const [isApproveTourPlanModalOpen, setIsApproveTourPlanModalOpen] = useState(false);
  const [viewingTourPlan, setViewingTourPlan] = useState<any>(null);
  const { user } = useAuth();
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const { submitTourPlan, updateTourPlan, updateTourPlanDeviation } = useWorkCalendar();

  const loadData = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      if (activeTab === 'holiday') {
        const holidayData = await getAllHolidays(user.tenant_id);
        setHolidays(holidayData);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, user]);

  useEffect(() => {
    if (!user) return;
    if (user && user.tenant_id) loadData();
  }, [loadData, user]);

  const handleSubmitHoliday = async (holidayData: HolidayFormData): Promise<void> => {
    try {
      await addHoliday(user.tenant_id, holidayData.zone_id, holidayData.holiday_date, holidayData.name);
      setIsAddHolidayModalOpen(false);
      await loadData();
    } catch (error) {
      console.error('Failed to save holiday:', error);
      toast.error('Failed to save holiday. Please try again.');
    }
  };

  const handleUpdateHoliday = async (data: HolidayFormData) => {
    try {
      await editHoliday(user.tenant_id, data.zone_id, data.holiday_date, data.name, editingHoliday!.id);
      setEditingHoliday(null);
      await loadData();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update holiday.');
    }
  };

  const handleSubmitTourPlan = async (tourPlanData: TourPlanRequest[]): Promise<void> => {
    try {
      const response = await submitTourPlan(tourPlanData);
      if (response.ok) {
        setIsAddTourPlanModalOpen(false);
        await loadData();
        toast.success('Tour plan created successfully!');
      } else {
        toast.error(response.error ?? 'Failed to create tour plan.');
      }
    } catch (error) {
      console.error('Failed to save tour plan:', error);
      toast.error('Failed to save tour plan. Please try again.');
    }
  };

  const handleDelete = async (id: number): Promise<void> => {
    try {
      await deleteHoliday(id);
      await loadData();
    } catch (error) {
      console.error('Failed to delete holiday:', error);
      toast.error('Failed to delete holiday. Please try again.');
    }
  };

  // Handle approve tour plan
  const handleApproveTourPlan = async (plan: any) => {
    try {
      let success = false;

      if (plan.deviation) {
        // ✅ Deviation approval
        success = await updateTourPlanDeviation(
          user.tenant_id,
          plan.id,
          plan.deviation.id,
          "approved",
          user.employee.id
        );
      } else {
        // ✅ Normal tour plan approval
        const payload = {
          id: plan.id,
          tenant_id: user.tenant_id,
          status: "approved",
          approved_by: user.employee.user_id,
        };
        success = await updateTourPlan([payload]);
      }

      if (success) {
        toast.success('Tour plan approved successfully.');
      } else {
        toast.error('Something went wrong!');
      }
    } catch (error) {
      console.error('Failed to approve tour plan:', error);
      toast.error('Failed to approve tour plan. Please try again.');
    }
  };

  // Handle reject tour plan
  const handleRejectTourPlan = async (plan: any) => {
    try {
      let success = false;

      if (plan.deviation) {
        success = await updateTourPlanDeviation(
          user.tenant_id,
          plan.id,
          plan.deviation.id,
          "rejected",
          user.employee.id
        );
      } else {
        const payload = {
          id: plan.id,
          tenant_id: user.tenant_id,
          status: "rejected",
          approved_by: user.employee.user_id,
        };
        success = await updateTourPlan([payload]);
      }

      if (success) {
        toast.success('Tour plan rejected successfully.');
      } else {
        toast.error('Something went wrong!');
      }
    } catch (error) {
      console.error('Failed to reject tour plan:', error);
      toast.error('Failed to reject tour plan. Please try again.');
    }
  };

  // Handle approve leave
  const handleApproveLeave = async (leave: any) => {
    try {
      const payload = {
        id: leave.id,
        tenant_id: user.tenant_id,
        status: "approved",
        approved_by: user.employee.user_id
      }
      const success = await updateLeave([payload]);
      if (success) {
        toast.success('Leave approved successfully.');
      } else {
        toast.error('Something went wrong!');
      }
    } catch (error) {
      console.error('Failed to approve leave:', error);
      toast.error('Failed to approve leave. Please try again.');
    }
  };

  // Handle reject leave
  const handleRejectLeave = async (leave: any) => {
    try {
      const payload = {
        id: leave.id,
        tenant_id: user.tenant_id,
        status: "rejected",
        approved_by: user.employee.user_id
      }
      const success = await updateLeave([payload]);
      if (success) {
        toast.success('Leave rejected successfully.');
      } else {
        toast.error('Something went wrong!');
      }
    } catch (error) {
      console.error('Failed to reject leave:', error);
      toast.error('Failed to reject leave. Please try again.');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!validTypes.includes(file.type)) {
      toast.warning('Please upload a valid CSV or Excel file.');
      event.target.value = '';
      return;
    }

    toast.info('File upload functionality will be implemented with actual CSV parsing.');
    event.target.value = '';
  };

  return (
    <PageLayout onLogout={onLogout} currentPage="holidays" onNavigate={onNavigate}>
      <div className="p-4 md:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Holiday & Work Calendar</h1>
                <p className="text-blue-600 mt-1">Manage holidays and tour plans</p>
              </div>
              {activeTab !== 'work' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => document.getElementById('import-holidays-input')?.click()}
                    className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import
                  </button>
                  <input
                    type="file"
                    id="import-holidays-input"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept=".csv,.xlsx,.xls"
                  />
                  <button
                    onClick={() => setIsAddHolidayModalOpen(true)}
                    className="flex items-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add
                  </button>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="mb-6">
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setActiveTab('holiday')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${activeTab === 'holiday'
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  Holiday Calendar
                </button>
                <button
                  onClick={() => setActiveTab('work')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${activeTab === 'work'
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  Work Calendar
                </button>
              </div>
            </div>

            {/* Content Based on Active Tab */}
            {activeTab === 'work' ? (
              <TourPlanList
                tourPlans={[]}
                isLoading={isLoading}
                onViewApproval={() => setIsApproveTourPlanModalOpen(true)}
                onAddTourPlan={() => setIsAddTourPlanModalOpen(true)}
              />
            ) : (
              <HolidayList
                holidays={holidays}
                isLoading={isLoading}
                onDelete={handleDelete}
                onEdit={(holiday) => {
                  setEditingHoliday(holiday);
                  setIsAddHolidayModalOpen(true);
                }}
              />

            )}

            <HolidayFormModal
              isOpen={isAddHolidayModalOpen}
              onOpenChange={(open) => {
                setIsAddHolidayModalOpen(open);
                if (!open) setEditingHoliday(null);
              }}
              onSubmit={editingHoliday ? handleUpdateHoliday : handleSubmitHoliday}
              title={editingHoliday ? 'Edit Holiday' : 'Add Holiday'}
              submitButtonText={editingHoliday ? 'Update Holiday' : 'Add Holiday'}
              type="public"
              initialData={editingHoliday}
            />

            <AddTourPlanModal
              isOpen={isAddTourPlanModalOpen}
              onOpenChange={setIsAddTourPlanModalOpen}
              onSubmit={handleSubmitTourPlan}
            />

            {/* Updated ApproveTourPlanModal */}
            <ApproveTourPlanModal
              isOpen={isApproveTourPlanModalOpen}
              onOpenChange={setIsApproveTourPlanModalOpen}
              onViewTourPlan={(plan) => {
                setViewingTourPlan(plan);
                setIsApproveTourPlanModalOpen(false);
              }}
              onApproveTourPlan={handleApproveTourPlan}
              onRejectTourPlan={handleRejectTourPlan}
              onViewLeave={(leave) => {
                setViewingTourPlan(leave);
                setIsApproveTourPlanModalOpen(false);
              }}
              onApproveLeave={handleApproveLeave}
              onRejectLeave={handleRejectLeave}
            />

            {viewingTourPlan && (
              <ViewTourPlanModal
                isOpen={true}
                onOpenChange={(open) => {
                  if (!open) {
                    setViewingTourPlan(null);
                    setIsApproveTourPlanModalOpen(true);
                  }
                }}
                tourPlan={viewingTourPlan}
              />
            )}
          </div>
        </div>
    </PageLayout>
  );
};

export default HolidaysPage;